import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  OnDestroy,
  signal,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LucideAngularModule } from 'lucide-angular';
import { NgChartsModule } from 'ng2-charts';
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart,
  Filler,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
} from 'chart.js';
import type { ChartData, ChartOptions } from 'chart.js';

// ng2-charts v5 doesn't ship a provideCharts helper — Chart.js controllers,
// scales, elements, and plugins must be registered explicitly before the
// first canvas mounts. Register-idempotent, so re-imports in another lazy
// route are safe. We only register what this page actually renders (line +
// bar) so the whole controller/scale/plugin registry doesn't ship in the
// initial JS payload for admins who never open this page.
Chart.register(
  LineController, BarController,
  LinearScale, CategoryScale,
  PointElement, LineElement, BarElement,
  Filler, Legend, Tooltip,
);
import { ToastrService } from 'ngx-toastr';
import { forkJoin } from 'rxjs';
import { Icons } from '../../../shared/helpers/icons';
import {
  Agent,
  FleetLatestPoint,
  FleetLogEntry,
  FleetSpeedDay,
} from '../../../shared/models/agent';
import { FleetService } from '../../../shared/services/fleet.service';
import { loadGoogleMaps } from '../../../shared/helpers/google-maps-loader';
import { SpinnerComponent } from '../../spinner/spinner.component';

/** Filter tokens the log-data endpoint accepts as v2-style observedData labels
 *  (the mapping back to connector types happens server-side). These MUST match
 *  the exact strings emitted by _TYPE_TO_OBSERVED_LABEL in
 *  fleet-nside-io/fleet-api/controllers/fleet_controller.py — anything else
 *  round-trips as "no match" and the filter silently returns zero rows.
 *
 *  Ordered by how often an operator wants to filter on them: motion / trip
 *  events first, driving-quality alerts, then connectivity/device events.
 *
 *  "Route" from the v2 legacy is intentionally absent: fleet-api never emits
 *  a route event type. Same for "Heartbeat" — keep-alive frames only renew
 *  the Redis registry, they aren't written to fleet_log at all. */
const EVENT_TYPE_LABELS = [
  'Trip Start',
  'Trip Stop',
  'Moving detected',
  'Moving stopped',
  'Speeding',
  'Acceleration threshold',
  'Deceleration threshold',
  'Enter-Geofence',
  'Exit-Geofence',
  'Emergency',
  'Driver Login',
  'Driver Logout',
  'Low Battery',
  'IP Changed',
  'In Coverage',
  'No GPS Fix',
  'Location Boundary',
] as const;
type EventTypeLabel = (typeof EVENT_TYPE_LABELS)[number];

/** Cast v2 observedData strings into their bucket for the counts row. Anything
 *  not in the map is counted in `total` only. Keys must match the server's
 *  observed-data labels verbatim; changing a label string on the server
 *  requires a matching update here.
 *
 *  Only events that map to a highlighted card at the top of the report land
 *  here. Filter-only labels (Driver Login, Low Battery, IP Changed…) are
 *  intentionally absent from this map — they still filter the table, they
 *  just don't need their own counter tile. */
const CATEGORY_OF: Record<string, keyof EventCounts> = {
  Speeding: 'speeding',
  'Trip Start': 'starts',
  'Trip Stop': 'stops',
  'Moving detected': 'starts',
  'Moving stopped': 'stops',
  'Enter-Geofence': 'geofence',
  'Exit-Geofence': 'geofence',
  'Acceleration threshold': 'accel',
  'Deceleration threshold': 'decel',
  Emergency: 'emergency',
};

interface EventCounts {
  total: number;
  speeding: number;
  starts: number;
  stops: number;
  geofence: number;
  accel: number;
  decel: number;
  emergency: number;
}

const EMPTY_COUNTS: EventCounts = {
  total: 0, speeding: 0, starts: 0, stops: 0,
  geofence: 0, accel: 0, decel: 0, emergency: 0,
};

@Component({
  selector: 'app-fleet-reports',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    LucideAngularModule,
    NgChartsModule,
    SpinnerComponent,
  ],
  templateUrl: './fleet-reports.component.html',
  styleUrl: './fleet-reports.component.css',
})
export class FleetReportsComponent implements AfterViewInit, OnDestroy {
  icons = Icons;
  eventTypeLabels = EVENT_TYPE_LABELS;

  private readonly destroyRef = inject(DestroyRef);

  @ViewChild('mapEl', { static: false }) mapEl?: ElementRef<HTMLDivElement>;

  // Google Maps state — imperative, mirrors pages/maps/maps.component pattern.
  // Kept private so the template can't reach in and mutate them.
  private map: google.maps.Map | null = null;
  private marker: google.maps.Marker | null = null;
  private mapReady = signal(false);
  /** Destroy sentinel: Angular does NOT null @ViewChild on teardown, so an
   *  async Google Maps script load that resolves after ngOnDestroy would
   *  otherwise stamp a live Map onto a detached div and leak the whole
   *  component. Set true in ngOnDestroy; every post-await handler bails
   *  out on it. */
  private destroyed = false;
  /** Cached (lat, lng) of the last successful reverse-geocode. Geocoder
   *  calls cost money and the tracker rarely moves between two Refresh
   *  clicks, so we short-circuit on identical coords. */
  private geocodedFor: { lat: number; lon: number } | null = null;

  /** Latest known GPS point from /api/fleet/points (live mode). Null while
   *  loading, and null-after-load means the agent has never reported. */
  latestPoint = signal<FleetLatestPoint | null>(null);
  loadingLatest = signal(false);
  /** Reverse-geocoded address for the latest point's lat/lng. Empty until
   *  the geocoder finishes; falls back to `lat, lng` in the template. */
  latestAddress = signal<string>('');

  /** Agent row from /admin/fleets/agents/:id — used to gate the Request GPS
   *  button on fleetVersion (only WLI trackers, versions 1 or 2, respond to
   *  the Piccolo GPS_REQUEST frame). Null until the fetch completes. */
  agent = signal<Agent | null>(null);
  /** True while the Request GPS POST is in flight. The button locks so a
   *  double-click can't fire two dispatches; the backend also throttles
   *  per-unit (60s) but locking client-side keeps the loop tight. */
  requestingGps = signal(false);

  // -----------------------------------------------------------------
  // Route + date range state
  // -----------------------------------------------------------------

  /** :agentId from the URL (route param). Signals-driven so the fetch effect
   *  reruns automatically if the user navigates between agents without a
   *  full component teardown. */
  agentId = signal<string>('');

  /** Date inputs bind here as ISO date strings (yyyy-mm-dd) — the datepickers
   *  emit that shape, and we normalise to full ISO datetimes before hitting
   *  fleet-api so the caller can't skew the range with a stray local TZ. */
  dateFrom = signal<string>(this.defaultDateFrom());
  dateTo = signal<string>(this.defaultDateTo());
  /** Event-type filter for the log table. Empty set = "all types". */
  selectedTypes = signal<Set<EventTypeLabel>>(new Set<EventTypeLabel>());

  // -----------------------------------------------------------------
  // Data state
  // -----------------------------------------------------------------

  logs = signal<FleetLogEntry[]>([]);
  speedDays = signal<FleetSpeedDay[]>([]);
  loading = signal(false);

  // -----------------------------------------------------------------
  // Derived widgets
  // -----------------------------------------------------------------

  /** Category counts across the currently-loaded log page. Recomputed on any
   *  log-refresh. The event log request pulls up to 500 rows; if a fleet
   *  ever routinely exceeds that we'd need server-side aggregation instead —
   *  see the effort note in the report-plan doc. */
  readonly counts = computed<EventCounts>(() => {
    const rows = this.logs();
    const out: EventCounts = { ...EMPTY_COUNTS };
    for (const row of rows) {
      out.total += 1;
      const label = row.observedData ?? '';
      const cat = CATEGORY_OF[label];
      if (cat) out[cat] += 1;
    }
    return out;
  });

  /** Log rows filtered by the type checkboxes. We do this client-side so the
   *  operator can flip filters without paying a round-trip — the initial
   *  fetch already pulled every type for the date range. */
  readonly filteredLogs = computed<FleetLogEntry[]>(() => {
    const selected = this.selectedTypes();
    if (selected.size === 0) return this.logs();
    return this.logs().filter(l =>
      selected.has((l.observedData ?? '') as EventTypeLabel),
    );
  });

  /** Chart.js dataset for the timeline: X = date (day granularity), Y = mph.
   *  We keep two series overlaid (avg + max) so the operator can see whether
   *  a day's average was dragged up by one spike or the whole day was fast.
   *
   *  fleet-api returns `days` sorted DESC (newest first) because the v2
   *  sidebar list wants newest-at-top. For a time-series chart we need
   *  ASC — reverse a shallow copy here so the X axis reads left-to-right
   *  in chronological order. The table below (`speedDays()` directly) keeps
   *  the newest-first ordering so the operator's eye lands on today first. */
  readonly speedChartData = computed<ChartData<'line'>>(() => {
    const daysAsc = [...this.speedDays()].reverse();
    return {
      labels: daysAsc.map(d => d.date),
      datasets: [
        {
          label: 'Avg speed (mph)',
          data: daysAsc.map(d => d.avgSpeed),
          borderColor: '#4f46e5',
          backgroundColor: 'rgba(79, 70, 229, 0.15)',
          tension: 0.3,
          pointRadius: 3,
        },
        {
          label: 'Max speed (mph)',
          data: daysAsc.map(d => d.maxSpeed),
          borderColor: '#dc2626',
          backgroundColor: 'rgba(220, 38, 38, 0.10)',
          tension: 0.3,
          pointRadius: 3,
        },
      ],
    };
  });

  readonly speedChartOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { position: 'top' },
      tooltip: { callbacks: { label: c => `${c.dataset.label}: ${c.parsed.y} mph` } },
    },
    scales: {
      y: { beginAtZero: true, title: { display: true, text: 'Speed (mph)' } },
      x: { title: { display: true, text: 'Date' } },
    },
  };

  /** Per-category bar chart of event counts. Reads straight off `counts()`
   *  so it stays synced with the log table. Categories align with what
   *  fleet-api actually emits — see EVENT_TYPE_LABELS. */
  readonly eventChartData = computed<ChartData<'bar'>>(() => {
    const c = this.counts();
    return {
      labels: ['Speeding', 'Trip Start', 'Trip Stop', 'Geofence', 'Accel', 'Decel', 'Emergency'],
      datasets: [{
        label: 'Events',
        data: [c.speeding, c.starts, c.stops, c.geofence, c.accel, c.decel, c.emergency],
        backgroundColor: [
          '#dc2626', '#22c55e', '#f59e0b',
          '#0ea5e9', '#eab308', '#14b8a6', '#f97316',
        ],
      }],
    };
  });

  readonly eventChartOptions: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      y: { beginAtZero: true, ticks: { precision: 0 } },
    },
  };

  constructor(
    private route: ActivatedRoute,
    private fleet: FleetService,
    private toastr: ToastrService,
  ) {
    this.route.paramMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(pm => {
        const id = pm.get('agentId') ?? '';
        this.agentId.set(id);
        if (id) {
          this.reload();
          this.reloadLatestPoint();
          this.reloadAgent();
        }
      });
  }

  /** Fetch the Agent row so the Request GPS button knows whether this
   *  tracker is a WLI (Piccolo protocol) unit or not — non-WLI agents
   *  can't respond to a manual GPS request frame and the button should
   *  reflect that. */
  private reloadAgent(): void {
    const id = this.agentId();
    if (!id) return;
    this.fleet.getAgent(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: agent => this.agent.set(agent),
        error: () => this.agent.set(null),
      });
  }

  /** True when Request GPS should be enabled — button is disabled while a
   *  request is in flight or when the agent isn't Fleet v2. Only v2 units
   *  route through the new fleet-connector control plane that dispatches
   *  Piccolo GPS_REQUEST commands over Redis; v1 (legacy connector) and
   *  every other version fall outside that path so a manual request has
   *  no way to reach the tracker. */
  readonly canRequestGps = computed(() => {
    if (this.requestingGps()) return false;
    const a = this.agent();
    if (!a) return false;
    return a.fleetVersion === 2;
  });

  requestGpsData(): void {
    const id = this.agentId();
    if (!id || !this.canRequestGps()) return;
    this.requestingGps.set(true);
    this.fleet.requestGpsData(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: res => {
          this.requestingGps.set(false);
          this.toastr.success(res.message);
          // Give the tracker a few seconds to reply through the ingest
          // pipeline, then pull the freshest point onto the map. 8s is
          // enough for the WLI cloud → connector → BigQuery hop under
          // normal load; the operator can hit Refresh manually if the
          // network is slow.
          setTimeout(() => {
            if (!this.destroyed) this.reloadLatestPoint();
          }, 8000);
        },
        error: err => {
          this.requestingGps.set(false);
          const msg = err?.error?.message || err?.message || 'Failed to send GPS request.';
          this.toastr.error(msg);
        },
      });
  }

  ngAfterViewInit(): void {
    // Kick off Google Maps init after the map div is in the DOM. If the
    // latest-point fetch has already returned by now (it usually will —
    // it's a small query), the marker gets placed as soon as the map is
    // ready; otherwise the reload handler drops it in later.
    this.initGoogleMap();
  }

  ngOnDestroy(): void {
    // Flip the sentinel BEFORE we tear the map down: any in-flight async
    // handler that resolves next tick (Google Maps script load, Geocoder
    // callback, RxJS subscribe if it wasn't cancelled in time) checks this
    // and short-circuits instead of stamping stale DOM refs.
    this.destroyed = true;
    // Marker + map objects hold DOM references; drop them explicitly so a
    // subsequent navigation back to this route creates a fresh map instead
    // of racing the reused one.
    if (this.marker) {
      this.marker.setMap(null);
      this.marker = null;
    }
    this.map = null;
    this.mapReady.set(false);
  }

  // -----------------------------------------------------------------
  // Actions
  // -----------------------------------------------------------------

  /** Fetch latest point independent of the reports date range — the current
   *  position doesn't care what the operator picked as their report window.
   *  The subscription is bound to `destroyRef` so a pending request that
   *  resolves after the operator navigates away doesn't touch dead signals
   *  or pop toasts on a page they've already left. */
  reloadLatestPoint(): void {
    const unitId = this.agentId();
    if (!unitId) return;
    this.loadingLatest.set(true);
    this.fleet.getFleetLatestPoint(unitId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: point => {
          this.latestPoint.set(point);
          this.loadingLatest.set(false);
          if (point) {
            this.applyLatestPointToMap(point);
            this.reverseGeocode(point.lat, point.lon);
          } else {
            this.latestAddress.set('');
            this.geocodedFor = null;
            if (this.marker) {
              this.marker.setMap(null);
              this.marker = null;
            }
          }
        },
        error: err => {
          this.loadingLatest.set(false);
          const msg = err?.error?.detail || err?.message || 'Failed to load latest position.';
          this.toastr.error(msg);
        },
      });
  }

  /** Parse the connector-encoded state suffix off `styleId`. Returns "unknown"
   *  when the point pre-dates the fleet-api rewrite (styleId in a legacy
   *  shape) so downstream UI can still render something meaningful. */
  statusOfLatest(): string {
    const point = this.latestPoint();
    if (!point?.styleId) return 'unknown';
    // Shape: fleet-api:<unitId>:<state>
    const parts = point.styleId.split(':');
    return parts.length >= 3 ? parts[parts.length - 1] : 'unknown';
  }

  /** Human-friendly label — matches what fleet-mobile shows in its detail
   *  popup so operators moving between the two see the same words. */
  statusLabel(): string {
    switch (this.statusOfLatest()) {
      case 'online': return 'Moving';
      case 'parked': return 'Parked';
      case 'speeding': return 'Speeding';
      case 'offline': return 'Offline';
      case 'geofence': return 'Inside geofence';
      default: return 'Unknown';
    }
  }

  /** Tailwind class for the status pill — kept in TS instead of in the
   *  template so the switch stays next to statusLabel() and can't drift
   *  out of sync with it. */
  statusClass(): string {
    switch (this.statusOfLatest()) {
      case 'online':   return 'bg-green-100 text-green-800';
      case 'parked':   return 'bg-gray-100 text-gray-700';
      case 'speeding': return 'bg-red-100 text-red-800';
      case 'offline':  return 'bg-slate-200 text-slate-600';
      case 'geofence': return 'bg-sky-100 text-sky-800';
      default:         return 'bg-gray-100 text-gray-700';
    }
  }

  /** Convert BQ speed (knots) → mph for display. Return "" so the template
   *  can conditionally hide the row when we don't have a value. */
  speedMph(): string {
    const s = this.latestPoint()?.speed;
    if (s === undefined || s === null) return '';
    return (s * 1.15078).toFixed(1);
  }

  private async initGoogleMap(): Promise<void> {
    if (!this.mapEl) return;
    try {
      const g = await loadGoogleMaps();
      // Angular does NOT null @ViewChild refs on component destroy, so the
      // pre-await `!this.mapEl` check can't detect a teardown that happened
      // while we were awaiting. Use the explicit `destroyed` sentinel that
      // ngOnDestroy sets. Without this a route-away that races the script
      // load leaks a Map + its resize/idle listeners on a detached div.
      if (this.destroyed || !this.mapEl) return;
      this.map = new g.maps.Map(this.mapEl.nativeElement, {
        zoom: 15,
        center: { lat: 39.5, lng: -98.35 }, // continental US centroid — replaced by first point
        mapTypeControl: true,
        streetViewControl: false,
        fullscreenControl: false,
      });
      this.mapReady.set(true);
      // If the latest-point request came back before the map was ready, drop
      // the marker in now that we can.
      const p = this.latestPoint();
      if (p) this.applyLatestPointToMap(p);
    } catch (err) {
      if (this.destroyed) return;
      this.toastr.error('Failed to load Google Maps. Refresh to retry.');
    }
  }

  private applyLatestPointToMap(point: FleetLatestPoint): void {
    if (!this.map || !this.mapReady()) return;
    const pos = { lat: point.lat, lng: point.lon };
    if (!this.marker) {
      this.marker = new google.maps.Marker({
        map: this.map,
        position: pos,
        // Rotate the standard forward-arrow SVG to match heading, mirroring
        // fleet-mobile's arrow marker. Falls back to a static circle when
        // heading is missing (parked / stale points don't carry direction).
        icon: this.markerIconFor(point),
        title: this.statusLabel(),
      });
    } else {
      this.marker.setPosition(pos);
      this.marker.setIcon(this.markerIconFor(point));
      this.marker.setTitle(this.statusLabel());
    }
    this.map.panTo(pos);
  }

  private markerIconFor(point: FleetLatestPoint): google.maps.Symbol {
    // Mirrors fleet-mobile's fleet-vehicle.component `getIcon` so operators
    // moving between admin-tools and the mobile app see the same shape and
    // colour semantics for the same state:
    //   - Speeding → amber (#eea236, iOS Fleet's "warning" tone)
    //   - Online (moving) → green (#34c759, iOS system green)
    //   - Parked / offline / geofence / unknown → gray tones so the arrow
    //     recedes visually when the vehicle isn't actively moving.
    // Fleet-mobile also falls back to the Style row's `icon` URL when one
    // is set — we don't have that here (no per-style fetch on the reports
    // page), so we always render the arrow.
    const state = this.statusOfLatest();
    const hasHeading =
      point.heading !== undefined && point.heading !== null && !Number.isNaN(point.heading);
    const color =
      state === 'speeding' ? '#eea236' :
      state === 'online'   ? '#34c759' :
      state === 'offline'  ? '#c7c7cc' :
      state === 'geofence' ? '#0ea5e9' :
                              '#8e8e93';
    return {
      path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
      fillColor: color,
      fillOpacity: 1,
      strokeColor: '#ffffff',
      strokeOpacity: 1,
      strokeWeight: 1.5,
      rotation: hasHeading ? Number(point.heading) : 0,
      scale: 5,
      anchor: new google.maps.Point(0, 2),
    };
  }

  private reverseGeocode(lat: number, lng: number): void {
    // Skip if we already geocoded these exact coords — Google Geocoder is
    // billed per call and pressing Refresh on a stationary tracker was
    // firing a new lookup for the same address every time. Comparing floats
    // for equality is safe here because the values come straight out of the
    // API response, never a math operation, so byte-identical repeat calls
    // hit exact `===`. Movement produces different floats and re-triggers.
    const cached = this.geocodedFor;
    if (cached && cached.lat === lat && cached.lon === lng && this.latestAddress()) {
      return;
    }
    // Fire-and-forget: if geocoding is slow or fails the address just stays
    // empty and the template falls back to "lat, lng". No user-facing error
    // for this since it's a nice-to-have decoration.
    loadGoogleMaps().then(g => {
      if (this.destroyed) return;
      const geocoder = new g.maps.Geocoder();
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (this.destroyed) return;
        if (status === 'OK' && results && results[0]) {
          this.latestAddress.set(results[0].formatted_address);
          this.geocodedFor = { lat, lon: lng };
        } else {
          this.latestAddress.set('');
          this.geocodedFor = null;
        }
      });
    }).catch(() => {
      if (this.destroyed) return;
      this.latestAddress.set('');
      this.geocodedFor = null;
    });
  }

  reload(): void {
    const unitId = this.agentId();
    if (!unitId) return;
    const from = this.toIsoStartOfDay(this.dateFrom());
    const to = this.toIsoEndOfDay(this.dateTo());
    if (!from || !to) {
      this.toastr.error('Pick a valid date range first.');
      return;
    }

    this.loading.set(true);
    // One combined subscribe so the loading flag flips off when BOTH widgets
    // are ready — the counts card would otherwise show '0' while the log
    // request is still in flight. Bound to destroyRef so a route-away
    // mid-flight doesn't stamp signals on the destroyed component.
    forkJoin({
      logs: this.fleet.getFleetLogData(unitId, from, to, [], 500),
      speed: this.fleet.getFleetSpeedSummary(unitId, from, to),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ logs, speed }) => {
          this.logs.set(logs.logs ?? []);
          this.speedDays.set(speed.days ?? []);
          this.loading.set(false);
        },
        error: err => {
          this.loading.set(false);
          const msg = err?.error?.detail || err?.message || 'Failed to load reports.';
          this.toastr.error(msg);
        },
      });
  }

  toggleType(label: EventTypeLabel): void {
    this.selectedTypes.update(s => {
      const next = new Set(s);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  }

  isTypeSelected(label: EventTypeLabel): boolean {
    return this.selectedTypes().has(label);
  }

  clearTypeFilters(): void {
    this.selectedTypes.set(new Set<EventTypeLabel>());
  }

  onDateFromChange(v: string): void {
    this.dateFrom.set(v);
  }

  onDateToChange(v: string): void {
    this.dateTo.set(v);
  }

  // -----------------------------------------------------------------
  // Helpers
  // -----------------------------------------------------------------

  /** Default range = last 7 days ending today. Matches what v2's default
   *  Fleet-Report-View request picks when the operator hasn't touched the
   *  datepicker yet. Uses local year/month/date instead of toISOString() so
   *  an operator in a negative-UTC offset late in the evening sees "today"
   *  matching their OS clock, not the UTC calendar. */
  private defaultDateFrom(): string {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return this.toYmdLocal(d);
  }

  private defaultDateTo(): string {
    return this.toYmdLocal(new Date());
  }

  /** `<input type="date">` emits/consumes `yyyy-mm-dd` in the operator's
   *  local calendar. Format a Date the same way so defaults line up with
   *  what the datepicker will show. */
  private toYmdLocal(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  /** Turn a `yyyy-mm-dd` datepicker value into the ISO UTC timestamp that
   *  corresponds to LOCAL midnight of that day. Naive `${v}T00:00:00Z`
   *  bakes in UTC midnight instead, which for an ET operator queries
   *  Sep 14 20:00 – Sep 15 19:59 when they picked Sep 15 – Sep 15. Feeding
   *  the Date constructor `Date(year, month-1, day)` builds a local
   *  midnight, then toISOString() converts to the UTC instant fleet-api
   *  expects. */
  private toIsoStartOfDay(v: string): string | null {
    if (!v) return null;
    const [y, m, d] = v.split('-').map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d, 0, 0, 0, 0).toISOString();
  }

  private toIsoEndOfDay(v: string): string | null {
    if (!v) return null;
    const [y, m, d] = v.split('-').map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d, 23, 59, 59, 999).toISOString();
  }
}
